/**
 * Angebotsnummern-Kreis — EINE gemeinsame Quelle für Format, Erkennung und
 * Folge-Nummern (Ollis "vergibt die Nummer nicht mehr automatisch").
 *
 * Hintergrund: Historisch würfelte der Client an sechs Stellen eine Zufallszahl
 * (`A-<Jahr>-<random 3-stellig>`). Ergebnis in Ollis Produktions-DB: dieselbe
 * Nummer viermal, dazwischen seine handvergebenen Formate (`A26/00025`,
 * `AN2600124`). Dieses Modul ersetzt den Zufall durch einen konfigurierbaren,
 * fortlaufenden Nummernkreis.
 *
 * Formatmodell: <praefix><jahresteil><trenner><laufende nummer>
 * - praefix:   frei, beginnt mit Buchstabe (z.B. "A", "AN", "A-")
 * - jahresteil: kein | 2-stellig | 4-stellig
 * - trenner:   0..1 Zeichen aus - / . _ und Leerzeichen
 * - laufende Nummer: mit führenden Nullen auf `digits` Stellen aufgefüllt
 *
 * Erkennungs-Regeln (BEWUSST nicht raten):
 * - 4-stelliger Jahresteil (19xx/20xx) wird auch OHNE Trenner erkannt
 *   ("A2026001"), weil das Jahrhundert ihn eindeutig macht.
 * - 2-stelliger Jahresteil wird NUR erkannt, wenn ein Trenner ihn von der
 *   laufenden Nummer abgrenzt ("A26/00025"). Ohne Trenner ("AN2600124") ist
 *   nicht entscheidbar, ob "26" Jahr oder Teil der Nummer ist — dann gilt die
 *   GESAMTE Ziffernfolge als laufende Nummer (nächste: AN2600125). Kein Raten;
 *   der Nutzer sieht die Deutung in der Vorschau und bestätigt sie.
 * - Vom System selbst erzeugte Alt-Nummern werden bei der Ableitung ignoriert:
 *   das Zufalls-Altformat `A-<4-stellig>-<3-stellig>` und Import-Platzhalter
 *   `IMP-…`. Sie beschreiben nicht das WUNSCH-Format des Nutzers.
 * - Die Ableitung liefert immer nur einen VORSCHLAG — übernommen wird er erst
 *   nach Bestätigung im Einstellungs-Formular (nie still).
 */

export type QuoteNumberYearMode = 'none' | 'two' | 'four';

export interface QuoteNumberFormat {
  prefix: string;
  yearMode: QuoteNumberYearMode;
  separator: string;
  digits: number;
}

export interface ParsedQuoteNumber {
  format: QuoteNumberFormat;
  /** Vierstelliges Jahr, falls ein Jahresteil erkannt wurde (2-stellig → 20xx). */
  year: number | null;
  seq: number;
  /** Ziffernfolge der laufenden Nummer wie geschrieben (Länge = Stellenzahl). */
  seqText: string;
}

export interface QuoteNumberCircleSuggestion extends QuoteNumberFormat {
  nextSeq: number;
  preview: string;
  /** Wie viele Bestands-Nummern das vorgeschlagene Format stützen. */
  sampleCount: number;
  /** Eine Beispiel-Nummer aus dem Bestand. */
  sample: string;
}

/** Zufalls-Altformat des Konstruktors: A-<Jahr 4-stellig>-<3-stellig>. */
export const LEGACY_RANDOM_NUMBER_PATTERN = /^A-\d{4}-\d{3}$/;

/** Import-Platzhalter (GAEB-/JSON-Import): IMP-… */
const IMPORT_PLACEHOLDER_PATTERN = /^IMP-/i;

const MAX_SEQ_DIGITS = 9;

export function formatQuoteNumber(
  format: QuoteNumberFormat,
  year: number,
  seq: number,
): string {
  const yearPart =
    format.yearMode === 'four'
      ? String(year)
      : format.yearMode === 'two'
        ? String(year % 100).padStart(2, '0')
        : '';
  const digits = Math.min(Math.max(Math.trunc(format.digits) || 1, 1), MAX_SEQ_DIGITS);
  return `${format.prefix}${yearPart}${format.separator}${String(seq).padStart(digits, '0')}`;
}

/**
 * Zerlegt eine Bestands-Nummer strikt nach den oben dokumentierten Regeln.
 * Nicht zerlegbare Nummern (frei formatiert, Nachträge, Import-Reste) → null.
 */
export function parseQuoteNumber(value: unknown): ParsedQuoteNumber | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;

  // Regel 1: 4-stelliges Jahr (19xx/20xx), Trenner optional.
  const four = trimmed.match(/^([A-Za-zÄÖÜäöü][A-Za-z0-9ÄÖÜäöü]*?[-/._ ]?)((?:19|20)\d{2})([-/._ ]?)(\d{1,9})$/);
  if (four) {
    const [, prefix, yearText, separator, seqText] = four;
    return {
      format: { prefix, yearMode: 'four', separator, digits: seqText.length },
      year: Number(yearText),
      seq: Number(seqText),
      seqText,
    };
  }

  // Regel 2: 2-stelliges Jahr NUR mit abgrenzendem Trenner.
  const two = trimmed.match(/^([A-Za-zÄÖÜäöü][A-Za-z0-9ÄÖÜäöü]*?[-/._ ]?)(\d{2})([-/._ ])(\d{1,9})$/);
  if (two) {
    const [, prefix, yearText, separator, seqText] = two;
    return {
      format: { prefix, yearMode: 'two', separator, digits: seqText.length },
      year: 2000 + Number(yearText),
      seq: Number(seqText),
      seqText,
    };
  }

  // Regel 3: kein Jahresteil — die abschließende Ziffernfolge ist die laufende
  // Nummer, alles davor der Präfix (inklusive eventuellem Trenner).
  const plain = trimmed.match(/^([A-Za-zÄÖÜäöü][A-Za-z0-9ÄÖÜäöü\-/._ ]*?)(\d{1,9})$/);
  if (plain) {
    const [, rawPrefix, seqText] = plain;
    // Trenner am Präfix-Ende als eigenes Feld führen, damit Format-Gruppen
    // stabil sind ("A-" + "" ≙ "A" + "-").
    const sepMatch = rawPrefix.match(/^(.*?)([-/._ ])$/);
    const prefix = sepMatch ? sepMatch[1] : rawPrefix;
    const separator = sepMatch ? sepMatch[2] : '';
    if (!prefix) return null;
    return {
      format: { prefix, yearMode: 'none', separator, digits: seqText.length },
      year: null,
      seq: Number(seqText),
      seqText,
    };
  }

  return null;
}

function formatGroupKey(format: QuoteNumberFormat): string {
  // digits bewusst NICHT im Schlüssel: unterschiedlich gepolsterte Nummern
  // desselben Formats ("A26/9" neben "A26/00025") gehören zusammen; die
  // Stellenzahl wird als Maximum der Gruppe bestimmt.
  return `${format.prefix} ${format.yearMode} ${format.separator}`;
}

/**
 * Leitet aus Bestands-Nummern einen Nummernkreis-VORSCHLAG ab (oder null,
 * wenn der Bestand kein belastbares Format hergibt — dann wird NICHT geraten).
 *
 * Bewertung je Format-Gruppe, in dieser Reihenfolge:
 * 1. Anzahl VERSCHIEDENER Nummern, 2. Anzahl Vorkommen insgesamt (Ollis
 * viermal vergebene A26/00025 zählt so stärker als ein einzelner Ausreißer),
 * 3. höchste laufende Nummer, 4. Gruppenschlüssel (deterministischer Ausgang).
 */
export function detectQuoteNumberCircle(
  numbers: ReadonlyArray<string | null | undefined>,
  currentYear: number = new Date().getFullYear(),
): QuoteNumberCircleSuggestion | null {
  type Group = {
    format: QuoteNumberFormat;
    parsed: ParsedQuoteNumber[];
    distinct: Set<string>;
    raw: number;
    maxSeq: number;
    sample: string;
  };
  const groups = new Map<string, Group>();

  for (const value of numbers || []) {
    if (typeof value !== 'string') continue;
    const trimmed = value.trim();
    if (!trimmed) continue;
    if (LEGACY_RANDOM_NUMBER_PATTERN.test(trimmed)) continue;
    if (IMPORT_PLACEHOLDER_PATTERN.test(trimmed)) continue;
    if (isNachtragNumber(trimmed)) continue;

    const parsed = parseQuoteNumber(trimmed);
    if (!parsed) continue;

    const key = formatGroupKey(parsed.format);
    let group = groups.get(key);
    if (!group) {
      group = {
        format: { ...parsed.format },
        parsed: [],
        distinct: new Set(),
        raw: 0,
        maxSeq: 0,
        sample: trimmed,
      };
      groups.set(key, group);
    }
    group.parsed.push(parsed);
    group.distinct.add(trimmed);
    group.raw += 1;
    group.maxSeq = Math.max(group.maxSeq, parsed.seq);
    group.format.digits = Math.max(group.format.digits, parsed.format.digits);
  }

  if (groups.size === 0) return null;

  const ranked = Array.from(groups.entries()).sort((a, b) => {
    const [keyA, ga] = a;
    const [keyB, gb] = b;
    if (gb.distinct.size !== ga.distinct.size) return gb.distinct.size - ga.distinct.size;
    if (gb.raw !== ga.raw) return gb.raw - ga.raw;
    if (gb.maxSeq !== ga.maxSeq) return gb.maxSeq - ga.maxSeq;
    return keyA < keyB ? -1 : keyA > keyB ? 1 : 0;
  });

  const [, winner] = ranked[0];

  // Nächste laufende Nummer: bei Jahresteil zählt nur das AKTUELLE Jahr —
  // ein Bestand nur aus Vorjahren startet im neuen Jahr wieder bei 1.
  const relevant = winner.format.yearMode === 'none'
    ? winner.parsed
    : winner.parsed.filter((p) => p.year === currentYear);
  const nextSeq = relevant.length === 0
    ? 1
    : Math.max(...relevant.map((p) => p.seq)) + 1;

  return {
    ...winner.format,
    nextSeq,
    preview: formatQuoteNumber(winner.format, currentYear, nextSeq),
    sampleCount: winner.raw,
    sample: winner.sample,
  };
}

// ============================================
// Nachtragsnummern: <Basisnummer>/N<laufend>
// ============================================

const NACHTRAG_SUFFIX_PATTERN = /^(.*)\/N(\d+)$/;

export function isNachtragNumber(value: unknown): boolean {
  return typeof value === 'string' && NACHTRAG_SUFFIX_PATTERN.test(value.trim());
}

export function buildNachtragNumber(baseNumber: string, n: number): string {
  return `${baseNumber.trim()}/N${n}`;
}

/** Nachtrags-Zähler einer Nummer relativ zur Basis; null, wenn sie kein Nachtrag dieser Basis ist. */
export function parseNachtragN(value: unknown, baseNumber: string): number | null {
  if (typeof value !== 'string') return null;
  const match = value.trim().match(NACHTRAG_SUFFIX_PATTERN);
  if (!match) return null;
  if (match[1] !== baseNumber.trim()) return null;
  const n = Number(match[2]);
  return Number.isSafeInteger(n) && n >= 1 ? n : null;
}

/** Höchstes vergebenes N zu einer Basis in einer Nummernliste (0 = keins). */
export function maxNachtragN(
  baseNumber: string,
  numbers: ReadonlyArray<string | null | undefined>,
): number {
  let max = 0;
  for (const value of numbers || []) {
    const n = parseNachtragN(value, baseNumber);
    if (n !== null && n > max) max = n;
  }
  return max;
}

// ============================================
// Doppelt vergebene Nummern sichtbar machen
// ============================================

/** Nummern, die in der Liste mehrfach vorkommen (Bestand wird NICHT umgeschrieben — nur markiert). */
export function findDuplicateOfferNumbers(
  numbers: ReadonlyArray<string | null | undefined>,
): Set<string> {
  const counts = new Map<string, number>();
  for (const value of numbers || []) {
    if (typeof value !== 'string') continue;
    const trimmed = value.trim();
    if (!trimmed) continue;
    counts.set(trimmed, (counts.get(trimmed) ?? 0) + 1);
  }
  const duplicates = new Set<string>();
  for (const [nr, count] of counts) {
    if (count > 1) duplicates.add(nr);
  }
  return duplicates;
}
